'use client'

import { useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { getCookie } from '@packages/lib/utlils/cookie-utils'

/** Pages that are OAuth intermediaries — never valid as a callback destination */
const IGNORED_REFERRERS = ['/authverification']

/** Pages that need callback URL tracking (user should return here after auth/submission) */
const CALLBACK_TRACKED_PATHS = ['register', '/degrees/email', '/degrees/prospectus', '/pgs/prospectus', '/pgs/email']

/** Check if a path is an ignored/intermediary route */
const isIgnoredPath = (path: string) =>
  IGNORED_REFERRERS.some((ignored) => path.includes(ignored))

/** Check if the current path is one that needs callback tracking */
const isCallbackTrackedPath = (path: string) =>
  CALLBACK_TRACKED_PATHS.some((tracked) => path.includes(tracked))

export default function RegisterRedirectListener() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const previousPath = useRef<string | null>(null)

  useEffect(() => {
    const fullUrl =
      pathname + (searchParams?.toString() ? `?${searchParams}` : '')

    if (isCallbackTrackedPath(pathname)) {
      // Force callbackUrl for careers-matcher pagetype (careers quiz → register → career-guides)
      const pageType = searchParams?.get('pagetype') || ''
      if (pageType === 'careers-matcher') {
        document.cookie = `callbackUrl=${encodeURIComponent('/career-guides/')}; path=/`
        previousPath.current = fullUrl
        return
      }

      const existingCallback = getCookie('callbackUrl')
      const decodedExisting = existingCallback
        ? decodeURIComponent(existingCallback)
        : null

      // Step 1: Resolve callback from previousPath or referrer
      let newCallback: string | null = null

      if (
        previousPath.current &&
        !isCallbackTrackedPath(previousPath.current) &&
        !isIgnoredPath(previousPath.current)
      ) {
        newCallback = previousPath.current
      }

      if (!newCallback && document.referrer) {
        try {
          const refUrl = new URL(document.referrer)
          const refPath = refUrl.pathname + refUrl.search
          if (
            refUrl.origin === window.location.origin &&
            !isIgnoredPath(refUrl.pathname) &&
            !isCallbackTrackedPath(refUrl.pathname)
          ) {
            newCallback = refPath
          }
        } catch {
          // ignore invalid referrer
        }
      }

      // Step 2: Decide whether to update the cookie
      if (newCallback) {
        // Real callback resolved (not authverification, not a tracked page itself)
        // Update only if it's different from existing
        if (decodedExisting !== newCallback) {
          document.cookie = `callbackUrl=${encodeURIComponent(newCallback)}; path=/`
        }
      } else if (!decodedExisting) {
        // No real callback and no existing cookie → fallback to home
        document.cookie = `callbackUrl=${encodeURIComponent('/')}; path=/`
      }
      // else: no real callback but existing cookie exists → keep it (OAuth flow protection)
    }

    previousPath.current = fullUrl
  }, [pathname, searchParams])

  return null
}
